/*
 * Reports a failed Module Federation dynamic import to telemetry (via
 * Analytics.noticeError) with a typed `error_category`, so CORS failures can be
 * told apart from 404s, dead remotes, parse errors, etc. directly in telemetry
 * (query `@error.error_category`).
 *
 * The URL to classify/report is parsed straight out of the import error: a
 * failed dynamic import throws `Failed to fetch dynamically imported module:
 * <url>`, and that <url> is the exact chunk the browser failed to fetch — more
 * precise than the static remote-entry URL when an exposed module's own chunk
 * (e.g. capital/routes) is what died. When the error carries no URL we fall
 * back to the remote-entry URL for the bare remote name.
 *
 * Fire-and-forget by design: classification runs an async network probe, but
 * this function returns immediately so it never blocks the caller's failure
 * path (notably the top-level await in featureFlagSlugs.js).
 */

import Analytics from '@/mixins/Analytics';
import { addAppSpecificData } from '@/utilities/analytics';
import { classifyImportError } from '@/utilities/mfe/classifyImportError';
import { remoteUrlFor } from '@/utilities/mfe/remoteUrls';

const URL_PATTERN = /(https?:\/\/[^\s'")]+)/;

/**
 * Pull the URL the browser failed to fetch out of the import error, walking the
 * `cause` chain since Vite wraps some failures. Returns null when no URL is found.
 * @param {Error} error
 * @returns {string|null}
 */
const failedUrlFrom = (error) => {
  let current = error;
  while (current) {
    const match = URL_PATTERN.exec(current.message ?? '');
    if (match) {
      return match[1];
    }
    current = current.cause;
  }
  return null;
};

/**
 * @param {string} specifier - the import specifier, e.g. 'capital/routes'
 * @param {Error} error - the error thrown by the failed import()
 */
const noticeImportError = async (specifier, error) => {
  try {
    // Prefer the exact failed URL from the error; fall back to the remote-entry
    // URL keyed by the bare remote name ('capital/routes' -> 'capital').
    const remoteUrl = failedUrlFrom(error) || remoteUrlFor(specifier.split('/')[0]);
    const category = await classifyImportError(remoteUrl, error);
    Analytics.methods.noticeError(
      error,
      addAppSpecificData({
        error_category: category,
        remote_name: specifier,
        remote_url: remoteUrl,
      }),
    );
  } catch {
    // Classification/reporting must never surface into the caller.
  }
};

export default noticeImportError;
